/**
 * Free-vs-Pro gating primitives — ported from
 * `design-handoff/tabvault-ds/ui_kits/vault/Vault Free Gating.dc.html`.
 *
 * Three reusable pieces the whole app shares:
 *   · ProLock        — the dashed "· PRO" lock chip used on gated rows
 *   · FeatureGate    — blurred/locked overlay wrapper with an Upgrade CTA
 *   · FreeVsProTable — the Free-vs-Pro comparison, reused by UpgradeModal
 *
 * Feature copy mirrors PricingPage.tsx exactly so the two surfaces never
 * drift. Tone is NO-PRESSURE: Free is generous, nothing nags, and the
 * upgrade modal always offers "Stay on Free" as a first-class action.
 * Everything styles with @vault/ui tokens; no hardcoded theme colors.
 */
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Badge, Button, Icon, Tag } from '@vault/ui';
import { UpgradeModal } from './UpgradeModal.js';

function sentence(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── ProLock — inline lock chip for gated rows ──────────────────────────────
export interface ProLockProps {
  /** Optional feature label before the plan word: "AI tags" → "AI TAGS · PRO". */
  label?: string;
  onUpgrade: () => void;
}

/**
 * The dashed "AI TAGS · PRO" chip from the gating screen — sits inline on a
 * session card / settings row and opens the upgrade flow on click.
 */
export function ProLock({ label, onUpgrade }: ProLockProps) {
  return (
    <Tag dashed onClick={onUpgrade}>
      <Icon name="lock" size={9} color="var(--amber-ink)" />
      <span style={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label ? `${label} · Pro` : 'Pro'}
      </span>
    </Tag>
  );
}

// ── FreeVsProTable — the comparison table ──────────────────────────────────
export interface FreeVsProTableProps {
  /**
   * Feature row to visually accent — matches a row key (`'version-history'`)
   * or any case-insensitive label fragment (`"version history"`).
   */
  highlight?: string;
}

/** `true` = included · `false` = not on this plan · string = value text. */
type CellValue = string | boolean;

interface PlanRow {
  key: string;
  label: string;
  free: CellValue;
  pro: CellValue;
}

/** Single source of plan copy — keep in lock-step with PricingPage.tsx. */
const ROWS: readonly PlanRow[] = [
  { key: 'local-saves', label: 'Local saves', free: 'Unlimited', pro: 'Unlimited' },
  { key: 'browser-sync', label: 'Synced to cloud', free: '1 browser', pro: 'All browsers, all devices' },
  { key: 'title-search', label: 'Title & URL search', free: true, pro: true },
  { key: 'full-text-search', label: 'Full-text + AI semantic search', free: false, pro: true },
  { key: 'ai-collections', label: 'AI collections that sort themselves', free: false, pro: true },
  { key: 'version-history', label: 'Version history of saved pages', free: false, pro: true },
  { key: 'priority-support', label: 'Priority support', free: false, pro: true },
];

function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function matchesRow(row: PlanRow, needle: string | undefined): boolean {
  if (!needle) return false;
  const n = norm(needle);
  if (!n) return false;
  return norm(row.key) === n || norm(row.label).includes(n);
}

export function FreeVsProTable({ highlight }: FreeVsProTableProps) {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
        color: 'var(--text-1)',
      }}
    >
      <div style={{ ...grid, background: 'var(--surface-0)', borderBottom: '1px solid var(--line)' }}>
        <span style={colHead}>What you get</span>
        <span style={colHead}>Free</span>
        <span style={{ display: 'inline-flex' }}>
          <Badge tone="pro" pill={false}>
            Pro
          </Badge>
        </span>
      </div>
      {ROWS.map((row, i) => {
        const hit = matchesRow(row, highlight);
        return (
          <div
            key={row.key}
            style={{
              ...grid,
              borderBottom: i < ROWS.length - 1 ? '1px solid var(--line)' : 'none',
              background: hit ? 'var(--accent-tint)' : 'transparent',
              boxShadow: hit ? 'inset 2px 0 0 var(--primary)' : 'none',
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: hit ? 600 : 400 }}>{row.label}</span>
            <PlanCell value={row.free} plan="free" />
            <PlanCell value={row.pro} plan="pro" />
          </div>
        );
      })}
    </div>
  );
}

function PlanCell({ value, plan }: { value: CellValue; plan: 'free' | 'pro' }) {
  if (value === false) return <Icon name="minus" size={12} color="var(--text-3)" />;
  if (value === true) {
    return <Icon name="check" size={12} color={plan === 'pro' ? 'var(--primary)' : 'var(--success)'} strokeWidth={2.2} />;
  }
  return (
    <span style={{ fontSize: 12, color: plan === 'pro' ? 'var(--text-1)' : 'var(--text-2)', fontWeight: plan === 'pro' ? 500 : 400 }}>
      {value}
    </span>
  );
}

const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1fr) 92px 148px',
  alignItems: 'center',
  gap: 10,
  padding: '9px 14px',
};
const colHead: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
};

// ── FeatureGate — blurred/locked overlay wrapper ───────────────────────────
export interface FeatureGateProps {
  isPro: boolean;
  /** Human name of the gated feature, e.g. "AI collections" — drives the overlay headline and the modal. */
  feature: string;
  children: ReactNode;
  /**
   * Optional static preview rendered (blurred) instead of `children` when
   * locked — pass one to avoid mounting live Pro components behind the gate.
   */
  fallback?: ReactNode;
}

/**
 * Pro members see `children` untouched. Free members see a blurred preview
 * under the gating screen's lock overlay ("Your tabs, sorted by AI" card):
 * blur(3px)/.55 preview, surface-fade gradient, lock square, Unlock CTA.
 * The CTA opens the self-contained UpgradeModal — no wiring required.
 */
export function FeatureGate({ isPro, feature, children, fallback }: FeatureGateProps) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  if (isPro) return <>{children}</>;

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 14 }}>
      <div aria-hidden style={{ filter: 'blur(3px)', opacity: 0.55, pointerEvents: 'none', userSelect: 'none' }}>
        {fallback ?? children}
      </div>
      <div style={overlay}>
        <span style={lockSquare}>
          <Icon name="lock" size={15} color="var(--amber-ink)" />
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>{sentence(feature)} is a Pro feature</span>
        <Button size="sm" onClick={() => setUpgradeOpen(true)}>
          Unlock with Pro
        </Button>
      </div>
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} feature={feature} />
    </div>
  );
}

const overlay: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: 16,
  textAlign: 'center',
  background: 'linear-gradient(transparent, var(--surface-1) 78%)',
};
const lockSquare: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: 11,
  background: 'var(--surface-2)',
  border: '1px solid var(--line-strong)',
};
